import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CarregadoresMoneyInput } from "@/components/CarregadoresMoneyInput";
import {
  nomeExibicaoModeloCarregador,
  type ModeloCarregadorVeicular,
} from "@/types/modelosCarregadores";
import {
  criarItemWallboxProposta,
  proximoModeloWallboxDisponivel,
  type WallboxPropostaItem,
} from "@/utils/wallboxItensPropostaUtils";

type DefaultsModelo = {
  potencia_kw: number;
  preco_equipamento: number;
};

type WallboxItensFieldProps = {
  itens: WallboxPropostaItem[];
  onChange: (itens: WallboxPropostaItem[]) => void;
  modelosDisponiveis: string[];
  incluirEquipamento: boolean;
  defaultsForModel: (modelo: string) => DefaultsModelo;
  /** Quando informado, a lista vem só do catálogo e a imagem do modelo entra na proposta. */
  catalogo?: ModeloCarregadorVeicular[];
};

export function WallboxItensField({
  itens,
  onChange,
  modelosDisponiveis,
  incluirEquipamento,
  defaultsForModel,
  catalogo,
}: WallboxItensFieldProps) {
  const somenteCatalogo = catalogo != null;
  const nomesCatalogo = (catalogo ?? []).map((modelo) => nomeExibicaoModeloCarregador(modelo));
  const proximoModelo = proximoModeloWallboxDisponivel(
    itens,
    somenteCatalogo ? nomesCatalogo : modelosDisponiveis
  );
  const modelosSelect = somenteCatalogo
    ? nomesCatalogo
    : modelosDisponiveis.length > 0
      ? modelosDisponiveis
      : ["Wallbox 7kW", "Wallbox 11kW", "Wallbox 22kW"];

  const modeloDoCatalogo = (nome: string) =>
    (catalogo ?? []).find((modelo) => nomeExibicaoModeloCarregador(modelo) === nome) ?? null;

  const atualizar = (id: string, patch: Partial<WallboxPropostaItem>) => {
    onChange(itens.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const aplicarModelo = (item: WallboxPropostaItem, modelo: string) => {
    const defaults = defaultsForModel(modelo);
    const doCatalogo = modeloDoCatalogo(modelo);
    atualizar(item.id, {
      modelo_carregador: modelo,
      modelo_id: doCatalogo?.id ?? null,
      imagem_url: doCatalogo?.imagemUrl ?? null,
      potencia_kw: doCatalogo?.potenciaNominalKw || defaults.potencia_kw,
      preco_equipamento: incluirEquipamento ? defaults.preco_equipamento : 0,
    });
  };

  const adicionar = () => {
    if (!proximoModelo) return;
    const defaults = defaultsForModel(proximoModelo);
    const doCatalogo = modeloDoCatalogo(proximoModelo);
    onChange([
      ...itens,
      criarItemWallboxProposta(
        proximoModelo,
        doCatalogo?.potenciaNominalKw || defaults.potencia_kw,
        1,
        incluirEquipamento ? defaults.preco_equipamento : 0,
        doCatalogo?.id ?? null,
        doCatalogo?.imagemUrl ?? null
      ),
    ]);
  };

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-medium">Carregadores da proposta</p>
          <p className="text-xs text-muted-foreground">
            {somenteCatalogo
              ? incluirEquipamento
                ? "Escolha o modelo do catálogo. Com a opção de incluir wallbox ligada, a proposta leva o nome, a imagem e as especificações."
                : "O modelo só entra no PDF quando a opção de incluir wallbox estiver ligada."
              : "Inclua potências diferentes na mesma proposta, por exemplo 7 kW e 22 kW."}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={adicionar}
          disabled={!proximoModelo}
        >
          <Plus className="mr-2 h-4 w-4" />
          {somenteCatalogo ? "Adicionar modelo" : "Adicionar potência"}
        </Button>
      </div>

      {somenteCatalogo && modelosSelect.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum modelo no catálogo. Cadastre os carregadores em Catálogo de Carregadores.
        </p>
      ) : itens.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum carregador. Clique em adicionar para começar.
        </p>
      ) : (
        <div className="space-y-3">
          {itens.map((item, index) => {
            const modelosOcupados = new Set(
              itens.filter((outro) => outro.id !== item.id).map((outro) => outro.modelo_carregador)
            );
            return (
              <div key={item.id} className="space-y-3 rounded-md border bg-background p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">
                    Carregador {index + 1}
                    <span className="ml-2 font-normal text-muted-foreground">
                      ({item.potencia_kw} kW)
                    </span>
                  </p>
                  {itens.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => onChange(itens.filter((outro) => outro.id !== item.id))}
                      aria-label={`Remover carregador ${index + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor={`wb-item-modelo-${item.id}`}>Modelo</Label>
                    <Select
                      value={item.modelo_carregador}
                      onValueChange={(modelo) => aplicarModelo(item, modelo)}
                    >
                      <SelectTrigger id={`wb-item-modelo-${item.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {modelosSelect.map((modelo) => (
                          <SelectItem
                            key={modelo}
                            value={modelo}
                            disabled={modelosOcupados.has(modelo)}
                          >
                            {modelo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`wb-item-qtd-${item.id}`}>Quantidade</Label>
                    <Input
                      id={`wb-item-qtd-${item.id}`}
                      type="number"
                      min={1}
                      step={1}
                      value={item.quantidade}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        atualizar(item.id, {
                          quantidade: Number.isFinite(n) && n >= 1 ? n : 1,
                        });
                      }}
                    />
                  </div>
                </div>
                {somenteCatalogo ? (
                  <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-2">
                    {item.imagem_url ? (
                      <img
                        src={item.imagem_url}
                        alt={item.modelo_carregador}
                        className="h-20 w-20 rounded object-contain bg-white"
                      />
                    ) : (
                      <div className="flex h-20 w-20 items-center justify-center rounded bg-white text-center text-[10px] text-muted-foreground">
                        Sem imagem
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {incluirEquipamento
                        ? "Esta imagem entra na proposta junto com as especificações do modelo."
                        : "Esta imagem não entra na proposta enquanto a opção de incluir wallbox estiver desligada."}
                    </p>
                  </div>
                ) : null}
                {incluirEquipamento && (
                  <CarregadoresMoneyInput
                    id={`wb-item-equip-${item.id}`}
                    label="Preço do equipamento (R$)"
                    value={Number(item.preco_equipamento) || 0}
                    onValueChange={(n) => atualizar(item.id, { preco_equipamento: n })}
                    helperText="Valor unitário deste modelo."
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
